import React from 'react';
import {
  Bullseye,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Content,
  Grid,
  GridItem,
  Spinner,
} from '@patternfly/react-core';
import { useAdminVerdaBalance } from '../../../hooks/useAdminVerda';
import { formatCurrencyAmount } from '../../../utils/money';
import { VerdaErrorAlert, VerdaLastFetched, VerdaRefreshButton } from './VerdaTabParts';

interface VerdaBalanceTabProps {
  /** Defers the fetch until the staff gate has passed (the page already renders <NotFound /> otherwise). */
  isStaff: boolean;
}

/**
 * Admin › Verda › Balance — the Verda account balance, read live from Verda on open and on Refresh.
 * Backend errors (not configured, bad credentials, rate limit, unreachable…) are rendered verbatim.
 */
const VerdaBalanceTab: React.FunctionComponent<VerdaBalanceTabProps> = ({ isStaff }) => {
  const { balance, loading, error, refetch } = useAdminVerdaBalance(isStaff);

  const renderBody = () => {
    if (error) {
      return <VerdaErrorAlert what="balance" message={error} />;
    }
    if (!balance) {
      return loading ? (
        <Bullseye>
          <Spinner size="lg" aria-label="Loading the Verda balance" />
        </Bullseye>
      ) : null;
    }
    const isNegative = Number(balance.amount) < 0;
    return (
      <>
        <Content
          component="p"
          className={`pf-v6-u-font-size-4xl pf-v6-u-font-weight-bold${isNegative ? ' pf-v6-u-text-color-status-danger' : ''}`}
        >
          {formatCurrencyAmount(balance.amount, balance.currency)}
        </Content>
        <VerdaLastFetched fetchedAt={balance.fetched_at} />
      </>
    );
  };

  return (
    <Grid hasGutter>
      <GridItem md={6} xl={4}>
        <Card>
          <CardHeader
            actions={{ actions: <VerdaRefreshButton loading={loading} onRefresh={refetch} what="balance" /> }}
          >
            <CardTitle>Account balance</CardTitle>
          </CardHeader>
          <CardBody>{renderBody()}</CardBody>
        </Card>
      </GridItem>
    </Grid>
  );
};

export default VerdaBalanceTab;
